import { Box, Flex, Text, useColorMode } from "@chakra-ui/react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../common/formatting/formatNumber";
import { FinancialData } from "../../../../common/services/brregService";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

interface FinancialsProps {
  financialData: FinancialData;
}

export const Financials = ({ financialData }: FinancialsProps) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();

  const textColor: string = colorMode === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)";
  const gridColor: string = colorMode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

  const barColors: string[] = [
    "rgba(75, 192, 192, 0.6)", // teal
    "rgba(255, 99, 132, 0.6)", // pink
    "rgba(54, 162, 235, 0.6)", // blue
    "rgba(255, 206, 86, 0.6)", // yellow
  ];

  const revenue = financialData.resultatregnskapResultat?.driftsresultat?.driftsinntekter?.sumDriftsinntekter || 0;
  const operatingCosts = financialData.resultatregnskapResultat?.driftsresultat?.driftskostnad?.sumDriftskostnad || 0;
  const operatingResult = financialData.resultatregnskapResultat?.driftsresultat?.driftsresultat || 0;
  const financialResult = financialData.resultatregnskapResultat?.finansresultat?.nettoFinans || 0;
  const annualResult = financialData.resultatregnskapResultat?.aarsresultat || 0;
  const equity = financialData.egenkapitalGjeld?.egenkapital?.sumEgenkapital || 0;
  const debt = financialData.egenkapitalGjeld?.gjeldOversikt?.sumGjeld || 0;

  const currency = financialData.valuta || "NOK";

  const barData = {
    labels: [t("Revenue"), t("Operating Costs"), t("Operating Result"), t("Financial Result"), t("Annual Result")],
    datasets: [
      {
        data: [revenue, operatingCosts, operatingResult, financialResult, annualResult],
        backgroundColor: barColors,
        borderColor: barColors.map((color) => color.replace("0.6", "1")),
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<"bar">) {
            if (tooltipItem.raw && typeof tooltipItem.raw === "number") {
              return formatNumber(tooltipItem.raw) + " " + currency;
            }
            return "";
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
          callback: function (tickValue: string | number) {
            if (typeof tickValue === "number") {
              return formatNumber(tickValue) + " " + currency;
            }
            return tickValue;
          },
        },
      },
      x: {
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
        },
      },
    },
  };

  const doughnutData = {
    labels: [t("Equity"), t("Debt")],
    datasets: [
      {
        data: [equity, debt],
        backgroundColor: [barColors[0], barColors[1]],
        borderColor: [barColors[0].replace("0.6", "1"), barColors[1].replace("0.6", "1")],
        borderWidth: 1,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: textColor,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<"doughnut">) {
            if (tooltipItem.raw && typeof tooltipItem.raw === "number") {
              return `${tooltipItem.label || ""}: ${formatNumber(tooltipItem.raw)} ${currency}`;
            }
            return "";
          },
        },
      },
    },
  };

  const formatCurrency = (value: number): string => {
    return formatNumber(value) + " " + currency;
  };

  const totalAssets = financialData.eiendeler?.sumEiendeler || equity + debt;

  const currentAssets = financialData.eiendeler?.omloepsmidler?.sumOmloepsmidler || 0;
  const fixedAssets = financialData.eiendeler?.anleggsmidler?.sumAnleggsmidler || 0;

  const assetsData = {
    labels: [t("Fixed Assets"), t("Current Assets")],
    datasets: [
      {
        data: [fixedAssets, currentAssets],
        backgroundColor: [barColors[2], barColors[3]],
        borderColor: [barColors[2].replace("0.6", "1"), barColors[3].replace("0.6", "1")],
        borderWidth: 1,
      },
    ],
  };

  const assetsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: textColor,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<"doughnut">) {
            if (tooltipItem.raw && typeof tooltipItem.raw === "number") {
              return `${tooltipItem.label || ""}: ${formatNumber(tooltipItem.raw)} ${currency}`;
            }
            return "";
          },
        },
      },
    },
  };

  return (
    <Flex flexDirection="column" rowGap="6" pt="6">
      {/* Financial Results Chart */}
      <Box backgroundColor="smBackground" border="1px solid" borderColor="smBorder" rounded="md" p="6">
        <Text fontSize="sm" fontWeight="semibold" pb="2">
          {t("Financial Overview")}
        </Text>
        <Flex width="full" justifyContent="space-around" columnGap="6">
          <Box height="200px" width="50%">
            <Bar data={barData} options={barOptions} />
          </Box>
          {/* Key Results Financials */}
          <Box
            width="50%"
            borderWidth="1px"
            borderRadius="md"
            p={3}
            bg={colorMode === "dark" ? "whiteAlpha.50" : "gray.50"}
          >
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t("Profit and Loss")}
            </Text>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Text fontSize="sm">{t("Revenue")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(revenue)}
              </Text>

              <Text fontSize="sm">{t("Operating Costs")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(operatingCosts)}
              </Text>

              <Text fontSize="sm">{t("Operating Result")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(operatingResult)}
              </Text>

              <Text fontSize="sm">{t("Financial Result")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(financialResult)}
              </Text>

              <Text fontSize="sm">{t("Annual Result")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(annualResult)}
              </Text>
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* Equity vs Debt Chart */}
      <Box backgroundColor="smBackground" border="1px solid" borderColor="smBorder" rounded="md" p="6">
        <Text fontSize="sm" fontWeight="semibold" mb={2}>
          {t("Equity vs Debt")}
        </Text>
        <Flex width="full" justifyContent="space-around" columnGap="6">
          <Box height="200px" width="50%" mb={4}>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </Box>
          {/* Key Balance Sheet Financials */}
          <Box
            width="50%"
            borderWidth="1px"
            borderRadius="md"
            p={3}
            bg={colorMode === "dark" ? "whiteAlpha.50" : "gray.50"}
          >
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t("Equity & Debt")}
            </Text>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Text fontSize="sm">{t("Equity")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(equity)}
              </Text>

              <Text fontSize="sm">{t("Debt")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(debt)}
              </Text>

              <Text fontSize="sm">{t("Sum Equity & Debt")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(financialData.egenkapitalGjeld?.sumEgenkapitalGjeld || 0)}
              </Text>
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* Assets Chart */}
      <Box backgroundColor="smBackground" border="1px solid" borderColor="smBorder" rounded="md" p="6">
        <Text fontSize="sm" fontWeight="semibold" mb={2}>
          {t("Assets Overview")}
        </Text>
        <Flex width="full" justifyContent="space-around" columnGap="6">
          <Box height="200px" width="50%" mb={4}>
            <Doughnut data={assetsData} options={assetsOptions} />
          </Box>

          {/* Key Assets Financials */}
          <Box
            width="50%"
            borderWidth="1px"
            borderRadius="md"
            p={3}
            bg={colorMode === "dark" ? "whiteAlpha.50" : "gray.50"}
          >
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t("Assets")}
            </Text>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Text fontSize="sm">{t("Fixed Assets")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(fixedAssets)}
              </Text>

              <Text fontSize="sm">{t("Current Assets")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(currentAssets)}
              </Text>

              <Text fontSize="sm">{t("Total Assets")}:</Text>
              <Text fontSize="sm" fontWeight="medium" textAlign="right">
                {formatCurrency(totalAssets)}
              </Text>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};
